import { useTranslation } from 'react-i18next'

// material
import Skeleton from '@mui/material/Skeleton'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { SxProps, Theme, useTheme } from '@mui/material/styles'

// components
import { Column } from './types'

export interface BaseTableBodyProps {
  columns: Column[]
  isLoading?: boolean
}

interface TableBodyProps extends BaseTableBodyProps {
  rows: any[]
  onStyleCell?: (row: any) => SxProps<Theme>
  onRowClick?: (row: any) => void
}

const TableBodyComponent = ({
  columns,
  rows,
  isLoading,
  onStyleCell,
  onRowClick,
}: TableBodyProps) => {
  const theme = useTheme()
  const { t } = useTranslation('common')

  return (
    <TableBody>
      {rows.map((row, index) => (
        <TableRow
          key={`${row.date}-${index}`}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={
            onRowClick
              ? { cursor: 'pointer', '&:hover': { backgroundColor: theme.palette.action.hover } }
              : undefined
          }
        >
          {columns.map((column, idx) => {
            const value = row[column.id]
            return (
              <TableCell
                key={`${row.date}-${index}-${idx}`}
                align={column.align || 'center'}
                colSpan={column.colSpan}
                rowSpan={column.rowSpan}
                onClick={column.preventRowClick ? (e) => e.stopPropagation() : undefined}
                sx={{
                  fontSize: 16,
                  color: 'text.primary',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  ...onStyleCell?.(row)
                }}
              >
                {column.format ? (value !== null ? column.format(value) : '-') : value}
              </TableCell>
            )
          })}
        </TableRow>
      ))}
      {(rows.length === 0 || isLoading) && (
        <TableRow>
          <TableCell colSpan={columns.length}>
            <Typography
              color={theme.palette.grey[500]}
              variant="body1"
              textAlign="center"
              padding={2}
            >
              {rows.length === 0 ? t('no.data') : <Skeleton width="100%" />}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  )
}

export default TableBodyComponent
